import { IntlShape, useIntl } from 'react-intl';

import { IOption } from '@/components/switchback/Select';
import { useLocaleSettings } from '@/hooks/useLocaleSettings';
import { ILocationSearchAutocompleteListOption } from '@/hooks/useLocationSearchAutocompleteOptions';

const SEARCH_LOCATION_POPULAR_DESTINATIONS: ILocationSearchAutocompleteListOption[] = [
  {
    label: 'Great Smoky Mountains National Park',
    sublabel: 'Tennessee',
    value: {
      address: 'Great Smoky Mountains National Park, TN',
    },
    groupName: 'popularDestinations',
  },
  {
    label: 'Yosemite National Park',
    sublabel: 'California',
    value: {
      address: 'Yosemite National Park, CA',
    },
    groupName: 'popularDestinations',
  },
  {
    label: 'Rocky Mountain National Park',
    sublabel: 'Colorado',
    value: {
      address: 'Rocky Mountain National Park, CO',
    },
    groupName: 'popularDestinations',
  },
  {
    label: 'Zion National Park',
    sublabel: 'Utah',
    value: {
      address: 'Zion National Park, UT',
    },
    groupName: 'popularDestinations',
  },
  {
    label: 'Grand Canyon National Park',
    sublabel: 'Arizona',
    value: {
      address: 'Grand Canyon National Park, AZ',
    },
    groupName: 'popularDestinations',
  },
];

const SEARCH_LOCATION_POPULAR_DESTINATIONS_CA: ILocationSearchAutocompleteListOption[] = [
  {
    label: 'Banff National Park',
    sublabel: 'Alberta',
    value: {
      address: 'Banff National Park, Alberta',
    },
    groupName: 'popularDestinations',
  },
  {
    label: 'Jasper National Park',
    sublabel: 'Alberta',
    value: {
      address: 'Jasper National Park, Alberta',
    },
    groupName: 'popularDestinations',
  },
  {
    label: 'Saguenay-St. Lawrence',
    sublabel: 'Québec',
    value: {
      address: 'Saguenay-St. Lawrence, Québec',
    },
    groupName: 'popularDestinations',
  },
  {
    label: 'Pacific Rim National Park',
    sublabel: 'British Columbia',
    value: {
      address: 'Pacific Rim National Park, British Columbia',
    },
    groupName: 'popularDestinations',
  },
  {
    label: 'Mount Revelstoke National Park',
    sublabel: 'British Columbia',
    value: {
      address: 'Mount Revelstoke National Park, British Columbia',
    },
    groupName: 'popularDestinations',
  },
];

const SEARCH_LOCATION_POPULAR_DESTINATIONS_NZ: ILocationSearchAutocompleteListOption[] = [
  {
    label: 'Coromandel Peninsula',
    sublabel: 'New Zealand',
    value: {
      address: 'Coromandel Peninsula',
    },
    groupName: 'popularDestinations',
  },
  {
    label: 'Tongariro National Park',
    sublabel: 'New Zealand',
    value: {
      address: 'Tongariro National Park',
    },
    groupName: 'popularDestinations',
  },
  {
    label: 'Abel Tasman National Park',
    sublabel: 'New Zealand',
    value: {
      address: 'Abel Tasman National Park',
    },
    groupName: 'popularDestinations',
  },
  {
    label: 'Egmont National Park',
    sublabel: 'New Zealand',
    value: {
      address: 'Egmont National Park',
    },
    groupName: 'popularDestinations',
  },
  {
    label: 'Nelson Lakes National Park',
    sublabel: 'New Zealand',
    value: {
      address: 'Nelson Lakes National Park',
    },
    groupName: 'popularDestinations',
  },
  {
    label: 'Hanmer Springs',
    sublabel: 'New Zealand',
    value: {
      address: 'Hanmer Springs',
    },
    groupName: 'popularDestinations',
  },
  {
    label: 'Wilson Bay',
    sublabel: 'New Zealand',
    value: {
      address: 'Wilson Bay',
    },
    groupName: 'popularDestinations',
  },
];

export const useSearchLocationPopularDestinations = () => {
  const { locale } = useLocaleSettings();

  if (locale === 'en-ca' || locale === 'fr-ca') {
    return SEARCH_LOCATION_POPULAR_DESTINATIONS_CA;
  }

  if (locale === 'en-nz') {
    return SEARCH_LOCATION_POPULAR_DESTINATIONS_NZ;
  }

  return SEARCH_LOCATION_POPULAR_DESTINATIONS;
};

export enum ESearchSheetContent {
  ADDRESS = 'address',
  DATES = 'dates',
  GUESTS = 'guests',
  HOME = 'home',
}

export enum SORT_OPTION {
  RECOMMENDED = 'recommended',
  PRICE_HIGH_TO_LOW = 'price-high-to-low',
  PRICE_LOW_TO_HIGH = 'price-low-to-high',
  NEW_LISTINGS = 'new-listings',
  AVERAGE_RATINGS = 'average-ratings',
  CLOSEST_FIRST = 'closest-first',
  TOP_RATED = 'top-rated',
}

export const useOptimizelySortOptions = () => {
  const intl = useIntl();

  return getOptimizelySortOptions(intl);
};

const getOptimizelySortOptions = (intl: IntlShape) => {
  const OPTIMIZELY_SORT_OPTIONS: Record<SORT_OPTION, IOption> = {
    [SORT_OPTION.RECOMMENDED]: {
      label: intl.formatMessage({
        defaultMessage: 'Recommended',
        id: 'axepoE',
        description: 'Search page sort option: default sort',
      }),
      shortLabel: intl.formatMessage({
        defaultMessage: 'Recommended',
        id: 'axepoE',
        description: 'Search page sort option: default sort',
      }),
      value: '',
    },
    [SORT_OPTION.PRICE_HIGH_TO_LOW]: {
      label: intl.formatMessage({
        defaultMessage: 'Price: High to low',
        id: 'Y+H3kh',
        description: 'Search page sort option: price descending',
      }),
      shortLabel: intl.formatMessage({
        defaultMessage: 'Price: High to low',
        id: 'Y+H3kh',
        description: 'Search page sort option: price descending',
      }),
      value: '-price',
    },
    [SORT_OPTION.PRICE_LOW_TO_HIGH]: {
      label: intl.formatMessage({
        defaultMessage: 'Price: Low to high',
        id: 'LI8AFv',
        description: 'Search page sort option: price ascending',
      }),
      shortLabel: intl.formatMessage({
        defaultMessage: 'Price: Low to high',
        id: 'LI8AFv',
        description: 'Search page sort option: price ascending',
      }),
      value: 'price',
    },
    [SORT_OPTION.NEW_LISTINGS]: {
      label: intl.formatMessage({
        defaultMessage: 'New listings',
        id: 'SPkRL2',
        description: 'Search page sort option: new listings',
      }),
      shortLabel: intl.formatMessage({
        defaultMessage: 'New listings',
        id: 'SPkRL2',
        description: 'Search page sort option: new listings',
      }),
      value: 'new_listings',
    },
    [SORT_OPTION.AVERAGE_RATINGS]: {
      label: intl.formatMessage({
        defaultMessage: 'Average ratings',
        id: 'XAtdEc',
        description: 'Search page sort option: average rating descending',
      }),
      shortLabel: intl.formatMessage({
        defaultMessage: 'Average ratings',
        id: 'XAtdEc',
        description: 'Search page sort option: average rating descending',
      }),
      value: '-rating',
    },
    [SORT_OPTION.CLOSEST_FIRST]: {
      label: intl.formatMessage({
        defaultMessage: 'Distance: Closest first',
        id: 'atFCob',
        description: 'Search page sort option: closest first descending',
      }),
      shortLabel: intl.formatMessage({
        defaultMessage: 'Closest first',
        id: 'YPU/l5',
        description: 'Search page sort option: closest first short descending',
      }),
      value: 'distance',
    },
    [SORT_OPTION.TOP_RATED]: {
      label: intl.formatMessage({
        defaultMessage: 'Top rated',
        id: 'ywr6Go',
        description: 'Search page sort option: top rated',
      }),
      shortLabel: intl.formatMessage({
        defaultMessage: 'Top rated',
        id: 'lNxKlF',
        description: 'Search page sort option: top rated short',
      }),
      value: '-num_reviews,-rating',
    },
  };

  return OPTIMIZELY_SORT_OPTIONS;
};
